import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { normalizeReceiptContact } from "@shared/receiptContact";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

export function ResendReceiptButton({ invoiceId, userRole }: { invoiceId: string; userRole?: string }) {
  const [open, setOpen] = useState(false);
  if (!userRole || !["sales", "finance", "admin"].includes(userRole)) return null;
  return <span onClick={e => e.stopPropagation()}>
    <Button type="button" size="sm" variant="outline" onClick={() => setOpen(true)}>Resend receipt</Button>
    <Dialog open={open} onOpenChange={setOpen}>
      {open && <ReceiptDialog invoiceId={invoiceId} onClose={() => setOpen(false)} />}
    </Dialog>
  </span>;
}

function ReceiptDialog({ invoiceId, onClose }: { invoiceId: string; onClose: () => void }) {
  const utils = trpc.useUtils();
  const preview = trpc.financeInvoices.receiptPreview.useQuery({ invoiceId }, { retry: false });
  const [channel, setChannel] = useState<"email" | "whatsapp">("email");
  const [contactValue, setContactValue] = useState("");
  const save = trpc.financeInvoices.saveReceiptContact.useMutation({
    retry: false,
    onSuccess(receipt) {
      utils.financeInvoices.receiptPreview.setData({ invoiceId }, receipt);
      // Customer cards, phone lists and other receipt previews share this saved contact.
      void utils.customers.invalidate();
      void utils.financeInvoices.invalidate();
      toast.success("Contact saved. You can now send the receipt.");
      setContactValue("");
    },
    onError(error) { toast.error(error.message); },
  });
  // Preserve the request identity on network retries; reopening is an explicit new request.
  const [requestId] = useState(() => crypto.randomUUID());
  const [attempted, setAttempted] = useState(false);
  const send = trpc.financeInvoices.resendReceipt.useMutation({
    retry: false,
    onSuccess(result) {
      const message = result.outcome === "awaiting_response"
        ? "WhatsApp invitation sent. The receipt will follow when the customer replies."
        : result.outcome === "submitted"
          ? "Receipt submitted for delivery."
          : "A resend was already requested. Check Communications before sending again.";
      toast.success(message);
      onClose();
    },
    onError(error) { toast.error(error.message); },
  });
  const data = preview.data;
  const recipient = channel === "email" ? data?.email : data?.phone;
  return <DialogContent>
    <DialogHeader>
      <DialogTitle>Resend receipt</DialogTitle>
      <DialogDescription>Send a copy of the confirmed payment receipt to the customer.</DialogDescription>
    </DialogHeader>
    {preview.isLoading ? <p>Checking confirmed payment...</p> : preview.error ? <p role="alert">{preview.error.message}</p> : data ? <>
      <p>{data.invoiceNumber} · SGD {data.amount}<br />{data.customerName}</p>
      <label className="grid gap-2">Send via
        <select className="rounded border p-2" value={channel} disabled={attempted || save.isPending} onChange={e => { setChannel(e.target.value as "email" | "whatsapp"); setContactValue(""); save.reset(); }}>
          <option value="email">Email</option><option value="whatsapp">WhatsApp</option>
        </select>
      </label>
      <p>{recipient ?? `No ${channel === "email" ? "email address" : "WhatsApp number"} on file.`}</p>
      {!recipient && !attempted && <div className="space-y-3 rounded border p-3">
        <p className="text-sm">Add the customer's {channel === "email" ? "email address" : "WhatsApp number"} here. Save will update their customer record.</p>
        <label className="grid gap-2 text-sm">{channel === "email" ? "Email address" : "WhatsApp number"}
          <Input type={channel === "email" ? "email" : "tel"} autoComplete={channel === "email" ? "email" : "tel"} value={contactValue} maxLength={channel === "email" ? 320 : 50} disabled={save.isPending} onChange={e => setContactValue(e.target.value)} placeholder={channel === "email" ? "customer@example.com" : "+65 9123 4567"} />
        </label>
        {channel === "whatsapp" && <p className="text-xs text-muted-foreground">For overseas numbers, include the country code (for example, +60).</p>}
        {contactValue && !normalizeReceiptContact(channel, contactValue) && <p className="text-sm" role="status">Enter a valid {channel === "email" ? "email address" : "WhatsApp number"}.</p>}
        {save.error && <p role="alert" className="text-sm">{save.error.message}</p>}
        <Button type="button" disabled={save.isPending || !normalizeReceiptContact(channel, contactValue)} onClick={() => save.mutate({ invoiceId, channel, value: contactValue })}>{save.isPending ? "Saving..." : "Save"}</Button>
      </div>}
      {channel === "whatsapp" && <p className="text-sm text-muted-foreground">If WhatsApp requires a reply first, Cleo will ask the customer to reply before sending the receipt.</p>}
      {send.error && <p role="alert" className="text-sm">{send.error.message}</p>}
      <Button type="button" disabled={!recipient || save.isPending || send.isPending || attempted} onClick={() => {
        setAttempted(true);
        send.mutate({ invoiceId, channel, requestId });
      }}>{send.isPending ? "Sending..." : "Send receipt"}</Button>
    </> : null}
  </DialogContent>;
}
